import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ImageIcon from "@mui/icons-material/Image";
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded";
import SortByAlphaRoundedIcon from "@mui/icons-material/SortByAlphaRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import { Link as RouterLink } from "react-router-dom";

import Container from "../../components/Container";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { fetchAllRegions } from "../../store/Vanlifer/regionSlice";
import {alpha, useTheme} from "@mui/material/styles";

type RegionLite = {
    regionId: number;
    name?: string | null;
    description?: string | null;
    imageIds?: number[] | null;
};

type SortMode = "RECOMMENDED" | "AZ" | "ZA" | "RANDOM";

const trimDescription = (text: string, max = 120) =>
    text.length > max ? text.slice(0, max) + "…" : text;

const getRegionImageUrl = (imageId?: number | null) => {
    if (!imageId) return null;
    const base = process.env.REACT_APP_API_URL as string;
    return `${base}/image/${imageId}`;
};

// deterministisches “Shuffle” mit Seed (damit nicht bei jedem Render alles springt)
function mulberry32(seed: number) {
    return function () {
        let t = (seed += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function seededShuffle<T>(items: T[], seed: number): T[] {
    const rand = mulberry32(seed);
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

export default function RegionsPage(): JSX.Element {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const dispatch = useAppDispatch();
    const regions = useAppSelector((s) => s.region.regions) as unknown as RegionLite[];

    const [isLoading, setIsLoading] = useState(false);

    // Sidebar state
    const [query, setQuery] = useState("");
    const [onlyWithImages, setOnlyWithImages] = useState(false);
    const [onlyWithDescription, setOnlyWithDescription] = useState(false);
    const [sortMode, setSortMode] = useState<SortMode>("RECOMMENDED");
    const [shuffleSeed, setShuffleSeed] = useState(1);

    useEffect(() => {
        let mounted = true;
        setIsLoading(true);
        Promise.resolve(dispatch(fetchAllRegions()))
            .finally(() => mounted && setIsLoading(false));
        return () => {
            mounted = false;
        };
    }, [dispatch]);

    const normalizedQuery = query.trim().toLowerCase();

    const filteredAndSorted = useMemo(() => {
        const list = regions ?? [];

        // Filter
        let out = list.filter((r) => {
            const name = (r.name ?? "").toLowerCase();
            const desc = (r.description ?? "").toLowerCase();
            const hasImages = (r.imageIds?.length ?? 0) > 0;
            const hasDesc = (r.description?.trim()?.length ?? 0) > 0;

            if (onlyWithImages && !hasImages) return false;
            if (onlyWithDescription && !hasDesc) return false;

            if (normalizedQuery) {
                return name.includes(normalizedQuery) || desc.includes(normalizedQuery);
            }
            return true;
        });

        // Sort
        const byNameAsc = (a: RegionLite, b: RegionLite) =>
            (a.name ?? "").localeCompare(b.name ?? "", "de", { sensitivity: "base" });

        const byNameDesc = (a: RegionLite, b: RegionLite) => byNameAsc(b, a);

        const byRecommended = (a: RegionLite, b: RegionLite) => {
            const aHas = (a.imageIds?.length ?? 0) > 0;
            const bHas = (b.imageIds?.length ?? 0) > 0;
            if (aHas && !bHas) return -1;
            if (bHas && !aHas) return 1;
            return byNameAsc(a, b);
        };

        if (sortMode === "AZ") out.sort(byNameAsc);
        if (sortMode === "ZA") out.sort(byNameDesc);
        if (sortMode === "RECOMMENDED") out.sort(byRecommended);
        if (sortMode === "RANDOM") out = seededShuffle(out, shuffleSeed);

        return out;
    }, [
        regions,
        normalizedQuery,
        onlyWithImages,
        onlyWithDescription,
        sortMode,
        shuffleSeed,
    ]);

    const activeFilterChips = useMemo(() => {
        const chips: Array<{ key: string; label: string; onDelete: () => void }> = [];
        if (onlyWithImages) chips.push({ key: "img", label: "Mit Bildern", onDelete: () => setOnlyWithImages(false) });
        if (onlyWithDescription) chips.push({ key: "desc", label: "Mit Beschreibung", onDelete: () => setOnlyWithDescription(false) });
        if (normalizedQuery) chips.push({ key: "q", label: `Suche: “${query.trim()}”`, onDelete: () => setQuery("") });
        return chips;
    }, [onlyWithDescription, onlyWithImages, normalizedQuery, query]);

    return (
        <Container>
            <React.Fragment>
                <Typography
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        letterSpacing: 1,
                    }}
                    gutterBottom
                    color="text.secondary"
                >
                    Reiseziele
                </Typography>

                <Box mb={2}>
                    <Typography
                        variant="h3"
                        component="h2"
                        color="text.primary"
                        fontWeight={700}
                        lineHeight={1.2}
                    >
                        Hier kannst Du neue {' '}
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.primary"
                            sx={{
                                background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                    mode === 'light'
                                        ? theme.palette.secondary.main
                                        : theme.palette.primary.main,
                                    0.3
                                )} 0%)`,
                            }}
                        >
                            Reiseziele entdecken
                        </Typography>
                    </Typography>
                    <Box mb={3}>
                        <Typography variant="h6" color="text.secondary">
                            Nutze die Filter, um Dich über Regionen und Gastgeber zu informieren.
                        </Typography>
                    </Box>
                </Box>
            </React.Fragment>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                {/* Layout: Sidebar + Results */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
                        gap: 2,
                        alignItems: "start",
                    }}
                >
                    {/* Sidebar */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                            position: { md: "sticky" },
                            top: { md: 16 },
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                            {filteredAndSorted.length} Ergebnis{filteredAndSorted.length === 1 ? "" : "se"}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                            Filter
                        </Typography>

                        <Box sx={{ mt: 1.25 }}>
                            <TextField
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Region suchen…"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRoundedIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                            <Chip
                                icon={<PhotoLibraryRoundedIcon />}
                                label="Mit Bildern"
                                clickable
                                color={onlyWithImages ? "primary" : "default"}
                                variant={onlyWithImages ? "filled" : "outlined"}
                                onClick={() => setOnlyWithImages((v) => !v)}
                                sx={{ fontWeight: 800 }}
                            />
                            <Chip
                                icon={<NotesRoundedIcon />}
                                label="Mit Beschreibung"
                                clickable
                                color={onlyWithDescription ? "primary" : "default"}
                                variant={onlyWithDescription ? "filled" : "outlined"}
                                onClick={() => setOnlyWithDescription((v) => !v)}
                                sx={{ fontWeight: 800 }}
                            />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                            Sortierung
                        </Typography>

                        <Select
                            size="small"
                            fullWidth
                            value={sortMode}
                            onChange={(e) => setSortMode(e.target.value as SortMode)}
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value="RECOMMENDED">Empfohlen (Bilder zuerst)</MenuItem>
                            <MenuItem value="AZ">Name A–Z</MenuItem>
                            <MenuItem value="ZA">Name Z–A</MenuItem>
                            <MenuItem value="RANDOM">Zufällig</MenuItem>
                        </Select>

                        <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                            <Chip
                                icon={<ShuffleRoundedIcon />}
                                label="Neu mischen"
                                clickable
                                disabled={sortMode !== "RANDOM"}
                                onClick={() => setShuffleSeed((s) => s + 1)}
                                sx={{ fontWeight: 800 }}
                            />
                            <Chip
                                label="Zurücksetzen"
                                clickable
                                onClick={() => {
                                    setQuery("");
                                    setOnlyWithImages(false);
                                    setOnlyWithDescription(false);
                                    setSortMode("RECOMMENDED");
                                }}
                                sx={{ fontWeight: 800 }}
                            />
                        </Stack>

                        {activeFilterChips.length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                    Aktiv
                                </Typography>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {activeFilterChips.map((c) => (
                                        <Chip
                                            key={c.key}
                                            label={c.label}
                                            onDelete={c.onDelete}
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 800 }}
                                        />
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Paper>

                    {/* Results */}
                    <Box>
                        {isLoading ? (
                            <Grid container spacing={2}>
                                {Array.from({ length: 9 }).map((_, i) => (
                                    <Grid item xs={12} sm={6} lg={4} key={i}>
                                        <Card elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "none", border: 0 }}>
                                            <Skeleton variant="rectangular" height={160} />
                                            <Box sx={{ p: 2 }}>
                                                <Skeleton variant="text" width="65%" />
                                                <Skeleton variant="text" width="90%" />
                                                <Skeleton variant="text" width="75%" />
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : filteredAndSorted.length === 0 ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 4,
                                    p: 3,
                                    border: 0,
                                    boxShadow: "none",
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }}>Keine Regionen gefunden</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Passe die Filter an oder setze sie zurück.
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={2}>
                                {filteredAndSorted.map((region) => {
                                    const title = region.name ?? "Ohne Name";
                                    const desc = trimDescription(region.description ?? "");
                                    const imgId = region.imageIds?.[0] ?? null;
                                    const imageUrl = getRegionImageUrl(imgId);
                                    const imgCount = region.imageIds?.length ?? 0;

                                    return (
                                        <Grid item xs={12} sm={6} lg={4} key={region.regionId}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    borderRadius: 3,
                                                    overflow: "hidden",
                                                    border: 0,
                                                    boxShadow: "none",
                                                    bgcolor: "background.paper",
                                                    transition: "transform 180ms ease",
                                                    "&:hover": { transform: "translateY(-2px)" },
                                                }}
                                            >
                                                <CardActionArea
                                                    component={RouterLink}
                                                    to={`/vanlifer/regions/${region.regionId}`}
                                                    sx={{ display: "block" }}
                                                >
                                                    {/* Image */}
                                                    <Box sx={{ height: 160, bgcolor: "action.hover" }}>
                                                        {imageUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={imageUrl}
                                                                alt={`Bild von der Region ${title}`}
                                                                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    height: "100%",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    flexDirection: "column",
                                                                    gap: 0.5,
                                                                    color: "text.secondary",
                                                                }}
                                                            >
                                                                <ImageIcon color="disabled" />
                                                                <Typography variant="caption" sx={{ fontWeight: 900 }}>
                                                                    Kein Bild
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <CardContent sx={{ p: 2 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                                                            {title}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                mt: 0.75,
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {desc || "Keine Beschreibung"}
                                                        </Typography>

                                                        {/*<Stack direction="row" spacing={1} sx={{ mt: 1.25 }} useFlexGap flexWrap="wrap">*/}
                                                        {/*    <Chip*/}
                                                        {/*        size="small"*/}
                                                        {/*        icon={<SortByAlphaRoundedIcon />}*/}
                                                        {/*        label={`ID: ${region.regionId}`}*/}
                                                        {/*        variant="outlined"*/}
                                                        {/*        sx={{ fontWeight: 800 }}*/}
                                                        {/*    />*/}
                                                        {/*    <Chip*/}
                                                        {/*        size="small"*/}
                                                        {/*        icon={<PhotoLibraryRoundedIcon />}*/}
                                                        {/*        label={imgCount > 0 ? `${imgCount} Bild${imgCount === 1 ? "" : "er"}` : "Keine Bilder"}*/}
                                                        {/*        color={imgCount > 0 ? "primary" : "default"}*/}
                                                        {/*        variant={imgCount > 0 ? "filled" : "outlined"}*/}
                                                        {/*        sx={{ fontWeight: 800 }}*/}
                                                        {/*    />*/}
                                                        {/*</Stack>*/}
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>
                </Box>
            </Box>
        </Container>
    );
}

